export type ApiErrorPayload = {
  error?: string;
  message?: string;
};

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; message: string; error?: string };

export type PhaseRow = {
  phase: string;
  status?: string;
  durationMs?: number;
  at?: string;
};

export type TrackRef = {
  id?: string;
  trackId?: string;
  persistentId?: string;
  persistentID?: string;
  catalogId?: string;
  name?: string;
  title?: string;
  artist?: string;
  artistName?: string;
  album?: string;
  artworkUrl?: string;
  artwork?: { url?: string };
  sourcePlaylists?: string[];
};

export type PlayerState = {
  playerState?: string;
  currentTrack?: TrackRef | null;
  outputNames?: string[];
  selectedOutputNames?: string[];
  selectedOutputs?: unknown[];
  outputs?: unknown[];
};

export type PlayerStatusPayload = {
  status?: string;
  updatedAt?: string;
  mode?: string;
  action?: string;
  message?: string;
  playerState?: string | PlayerState;
  currentTrack?: TrackRef | null;
  outputNames?: string[];
  selectedOutputNames?: string[];
  selectedOutputs?: unknown[];
  outputs?: unknown[];
  phaseTimings?: Record<string, number>;
};

export type CasaBootstrap = {
  ok?: boolean;
  playerState?: PlayerState;
  currentTrack?: TrackRef | null;
  outputNames?: string[];
  outputs?: unknown[];
  selectedOutputNames?: string[];
  selectedOutputs?: unknown[];
  routeReady?: boolean;
  ready?: boolean;
  volumeGuard?: unknown;
  message?: string;
};

export type WarmupResult = {
  ok?: boolean;
  routeReady?: boolean;
  ready?: boolean;
  routePhase?: string;
  phase?: string;
  debounced?: boolean;
  durationMs?: number;
  volumeGuard?: unknown;
  message?: string;
};

export type PlayAccepted = {
  jobId: string;
  state: string;
  phase: string;
  statusUrl?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PlayJob = {
  jobId: string;
  state: string;
  phase: string;
  playSource?: string;
  phaseTimings?: Record<string, number>;
  phases?: PhaseRow[];
  result?: {
    ok?: boolean;
    playSource?: string;
    queue?: { source?: string; reason?: string; remaining?: number };
    phaseTimings?: Record<string, number>;
    playedTrack?: TrackRef;
    playerState?: PlayerState;
    currentTrack?: TrackRef;
    volumeGuard?: unknown;
    routeSource?: string;
    routeReady?: boolean;
  } | null;
  error?: { message?: string; phase?: string; timeoutMs?: number } | string | null;
};

export type FeedbackPayload = {
  trackId: string;
  sourcePlaylists?: string[];
  favorite?: boolean;
  disliked?: boolean;
};

export type FeedbackResult = {
  trackId?: string;
  catalogId?: string;
  trackName?: string;
  playlistName?: string;
  playlistAdded?: boolean;
  playlistMatched?: boolean;
  alreadyInPlaylist?: boolean;
  catalogLibraryAdded?: boolean;
  favoriteUpdated?: boolean;
  dislikedUpdated?: boolean;
  favorite?: boolean;
  disliked?: boolean;
  message?: string;
  ratingError?: string;
};

export type CasaOutput = {
  name?: string;
  kind?: string;
  available?: boolean;
  active?: boolean;
  selected?: boolean;
  supportsAudio?: boolean;
  soundVolume?: number;
  volume?: number;
};

export type OutputsResult = {
  ok?: boolean;
  requestedOutputNames?: string[];
  selectedOutputNames?: string[];
  audioOutputs?: CasaOutput[];
  outputs?: CasaOutput[];
  ready?: boolean;
  routeReady?: boolean;
  phase?: string;
  routePhase?: string;
  attempts?: number;
  missingOutputNames?: string[];
  message?: string;
  volumeGuard?: unknown;
};

export type VolumesResult = {
  ok?: boolean;
  requestedOutputNames?: string[];
  selectedOutputNames?: string[];
  audioOutputs?: CasaOutput[];
  outputs?: CasaOutput[];
  requestedVolume?: number | null;
  volume?: number | null;
  deviceVolumes?: Array<{ name?: string; requestedVolume?: number; volume?: number; capped?: boolean }>;
  volumeGuard?: {
    enabled?: boolean;
    maxVolume?: number;
    capped?: boolean;
    volume?: number | null;
    requestedVolume?: number | null;
    message?: string;
  };
  message?: string;
};

export type HealthResult = {
  ok?: boolean;
  service?: string;
  mode?: string;
  host?: string;
  port?: number;
  capabilities?: string[];
  appleMusicBridgeConfigured?: boolean;
  framePiConfigured?: boolean;
  updatedAt?: string;
  message?: string;
};

export type AuditEvent = {
  event?: string;
  kind?: string;
  path?: string;
  upstreamPath?: string;
  action?: string;
  result?: string;
  phase?: string;
  status?: string;
  durationMs?: number;
  timeoutMs?: number;
  error?: string;
  message?: string;
  requestId?: string;
  at?: string;
  time?: string;
  timestamp?: string;
  routeReady?: boolean;
  volumeGuard?: unknown;
  [key: string]: unknown;
};

export type AuditRecentResult = {
  ok?: boolean;
  service?: string;
  mode?: string;
  redacted?: boolean;
  retained?: number;
  count?: number;
  limit?: number;
  events?: AuditEvent[];
};

export type AuthConfig = {
  allowedEmail?: string;
  bridgeUrl?: string;
  googleClientId?: string;
};

export type AuthSession = {
  authenticated?: boolean;
  user?: {
    email?: string;
    name?: string;
    avatarUrl?: string | null;
  };
};

const API_BASE = "/api/casa-sonora";

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      credentials: "same-origin",
      ...init,
      headers: {
        accept: "application/json",
        ...(init.body ? { "content-type": "application/json; charset=utf-8" } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (error) {
    return {
      ok: false,
      status: 0,
      message: error instanceof Error ? error.message : "No pude llegar a Casa Sonora.",
    };
  }

  const payload = (await response.json().catch(() => ({}))) as T & ApiErrorPayload;
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: payload.error,
      message: payload.message || payload.error || `Casa Sonora devolvió ${response.status}.`,
    };
  }

  return { ok: true, data: payload };
}

export function getBootstrap(): Promise<ApiResult<CasaBootstrap>> {
  return requestJson<CasaBootstrap>("/bootstrap");
}

export function warmupCasaSonora(): Promise<ApiResult<WarmupResult>> {
  return requestJson<WarmupResult>("/warmup", {
    method: "POST",
    body: JSON.stringify({ outputNames: [] }),
  });
}

export function playMoodAsync(prompt: string): Promise<ApiResult<PlayAccepted>> {
  return requestJson<PlayAccepted>("/play", {
    method: "POST",
    body: JSON.stringify({
      async: true,
      prompt,
      outputNames: [],
    }),
  });
}

export function getPlayJob(jobId: string): Promise<ApiResult<PlayJob>> {
  return requestJson<PlayJob>(`/jobs/${encodeURIComponent(jobId)}`);
}

export function getPlayerState(): Promise<ApiResult<PlayerStatusPayload>> {
  return requestJson<PlayerStatusPayload>("/player");
}

export function setPlayerAction(action: "play" | "pause"): Promise<ApiResult<PlayerStatusPayload>> {
  return requestJson<PlayerStatusPayload>("/player", {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

export function sendFeedback(payload: FeedbackPayload): Promise<ApiResult<FeedbackResult>> {
  return requestJson<FeedbackResult>("/feedback", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function setCasaSonoraOutputs(outputNames: string[]): Promise<ApiResult<OutputsResult>> {
  return requestJson<OutputsResult>("/outputs", {
    method: "POST",
    body: JSON.stringify({ outputNames }),
  });
}

export function setCasaSonoraVolume(input: {
  outputNames?: string[];
  volume?: number;
  deviceVolumes?: Array<{ name: string; volume: number }>;
}): Promise<ApiResult<VolumesResult>> {
  return requestJson<VolumesResult>("/volumes", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getCasaSonoraHealth(): Promise<ApiResult<HealthResult>> {
  return requestJson<HealthResult>("/health");
}

export function getAuditRecent(limit = 12): Promise<ApiResult<AuditRecentResult>> {
  return requestJson<AuditRecentResult>(`/audit/recent?limit=${encodeURIComponent(String(limit))}`);
}

export async function getAuthConfig(next = "/"): Promise<ApiResult<AuthConfig>> {
  let response: Response;
  try {
    response = await fetch(`/api/auth/config?next=${encodeURIComponent(next)}`, {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
  } catch (error) {
    return {
      ok: false,
      status: 0,
      message: error instanceof Error ? error.message : "No pude preparar el acceso.",
    };
  }

  const payload = (await response.json().catch(() => ({}))) as AuthConfig & ApiErrorPayload;
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: payload.error,
      message: payload.message || payload.error || `Auth devolvió ${response.status}.`,
    };
  }

  return { ok: true, data: payload };
}

export async function signInWithGoogleCredential(credential: string): Promise<ApiResult<AuthSession>> {
  let response: Response;
  try {
    response = await fetch("/api/auth/google", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({ credential }),
    });
  } catch (error) {
    return {
      ok: false,
      status: 0,
      message: error instanceof Error ? error.message : "No pude completar el login.",
    };
  }

  const payload = (await response.json().catch(() => ({}))) as AuthSession & ApiErrorPayload;
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: payload.error,
      message: payload.message || payload.error || `Auth devolvió ${response.status}.`,
    };
  }

  return { ok: true, data: payload };
}
